'use client';
/**
 * 动态获取商品数据 (使用 SWR)
 * 用于在组件挂载时获取商品数据，支持预览模式和正常模式，并利用 SWR 实现数据缓存与滚动位置保留
 *
 * 登录态变化触发的二次刷新由 SWR 的 mutate 完成；
 * 由 useSWR 配置的 keepPreviousData=true 保证刷新期间保留旧数据，
 * 避免板块在二次刷新期间退回骨架或被整体卸载，杜绝页面大范围闪动 / 全页骨架重出现。
 */

import { useStore } from '@/providers/app-store-provider';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { serializeKey } from '@/utils/swr/keys';
import { useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';

interface UseFetchDynamicProductsParams {
  componentId?: string;
  componentBlockId?: string | number;
  currencyName?: string;
  enableAuthRefetch?: boolean;
  inView?: boolean;
  hasProducts?: boolean;
}

export function useFetchDynamicProducts<T>({
  componentId,
  componentBlockId,
  currencyName,
  enableAuthRefetch = true,
  inView = true,
  hasProducts = false,
}: UseFetchDynamicProductsParams) {
  const token = useStore((state) => state.token);
  const isLoggedIn = !!token;

  const isPreview =
    typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);
  const status = isPreview ? 2 : 1;

  // 将 Key 设计为全局 Fetcher 支持的 [URL, Params] 格式
  // 我们使用 params 字符串化并排序的方式，保证 Key 序列化后的绝对稳定
  const requestParams = {
    data: {
      component_id: componentId!,
      id: Number(componentBlockId),
      currency: currencyName || 'USD',
      status,
    },
  };

  const swrKeyString = serializeKey(['POST:/api/block/get_product_data', requestParams]);

  // 1. 获取全局配置中的 cache
  const { cache } = useSWRConfig();

  // 2. 为了提前判断 hasCache，我们用自己算出来的、将要传给 SWR 的 swrKeyString 去内存里探测
  const cachedState = swrKeyString ? cache.get(swrKeyString) : undefined;
  const hasCache = cachedState && cachedState.data !== undefined;

  // 3. 只有在 (可见 inView 或者 已有缓存 hasCache)、没有预设静态商品数据，且组件ID有效时，才发起请求
  const shouldFetch = (inView || hasCache) && !hasProducts && !!componentId && !!componentBlockId;

  // 4. 最终传递给 SWR 的 Key (挂起请求时传 null)
  const swrKey = shouldFetch ? swrKeyString : null;

  const {
    data: dynamicProductsData,
    error,
    isLoading,
    mutate,
  } = useSWR<T | null>(swrKey, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    dedupingInterval: 300000,
    keepPreviousData: true,
  });

  const authStatusRef = useRef(isLoggedIn);

  useWhenTokenReady(() => {
    if (authStatusRef.current === isLoggedIn) {
      return;
    }

    if (!enableAuthRefetch) {
      authStatusRef.current = isLoggedIn;
      return;
    }

    // 首次拉取尚未完成（无数据 + 无缓存）时不抢跑：保留 ref 不变，
    // 等首次拉取落地后下一轮 effect 自然触发刷新，
    // 避免「ref 已消费但 mutate 在无可用数据时无意义」导致后续无法再触发二次刷新
    if (dynamicProductsData == null && !hasCache) {
      return;
    }

    authStatusRef.current = isLoggedIn;
    // SWR 的 keepPreviousData=true 让二次刷新期间保留旧数据，
    // 板块不会退回骨架 / 不会被卸载，二次刷新对用户无感
    mutate();
  }, [isLoggedIn, enableAuthRefetch, mutate, dynamicProductsData, hasCache]);

  return {
    dynamicProductsData: dynamicProductsData || null,
    isLoading,
    hasAttemptedFetch: dynamicProductsData !== undefined || !!error,
  };
}
